import React, { useState, useEffect, useMemo } from 'react';
import { get, size } from 'global/lib/lodash';
import Filters from './filters';
import { filterData } from './utils';
import { useFilter } from 'global/hooks/useFilter';
import { _size } from '../../../global/utils';

export default (props) => {
  const { isCourse,
    isTemplate,
    assessment,
    activeSc,
    scorecardIndex,
    assessmentLoading,
    loading,
    status,
    updateScores,
    scoredata,
    setTreed,
    activeEdit,
    scorecards,
    setScorecardIndex,
    searchString,
    setSearchString,
    filter,
    setFilter,
    hasSubScores 
  } = props;

  const filterHook = useFilter();

  const defaultFilters = useMemo(() => ({
    value: 'any',
    hideNa: !(isCourse || ['draft', 'template'].includes(get(assessment, 'data.status', 'draft'))),
    search: '',
    listGroup: isCourse || isTemplate ? 'category' : get(assessment, `data.scorecards.${scorecardIndex}.config.defaultDisplay`) === 'realm' ? 'location' : 'category',
    onlyLoc: false,
    useLocation: ['draft', 'template'].includes(get(assessment, 'data.status', 'draft')),
  }), [scorecardIndex, assessment, isCourse, isTemplate]);

  const [filterLoading, setFilterLoading] = useState(assessmentLoading);
  const [filterLocal, setFilterLocal] = useState(defaultFilters);
  const [filterBtns, setFilterBtns] = useState([]);
  const [origStatus, setOrigStatus] = useState(get(assessment, 'data.status', 'draft'));

  useEffect(() => {
    // this triggers when opening to the scores tab before opening other parts of assessments
    // (because it will assume the assessment is in "Draft" state until it's loaded)
    // and also triggers when you change assessment info while scoring (change status)
    buildFilters();
    if (!assessmentLoading && status !== origStatus) {
      updateScores();
      setOrigStatus(status);
    }
  }, [assessmentLoading, status, scorecardIndex]);

  useEffect(() => {
    if (!loading && _size(filterHook.user.allrealms)) {
      runFilter();
    }
  }, [filterLocal, searchString, loading, filterHook.user.allrealms]);

  const buildFilters = () => {
    setFilterLocal(defaultFilters);

    const options = [
      { text: 'All Scores', val: 'any' },
      { text: 'Actionable', val: 'act' },
      { text: 'High Scores', val: 'high' },
      { text: 'Low Scores', val: 'low' },
    ];
    
    const valLabels = get(activeSc, `config.scoreValueLabel`, []);

    if (get(activeSc, 'config.allowNA')) {
      options.push({ text: 'N/A', val: 'na' });
    }
    if (get(activeSc, 'config.allowNO')) {
      const NOLabel = valLabels && valLabels.find((i) => i.value === -1);
      options.push({ text: get(NOLabel, 'label') || 'N/O', val: -1 });
    }
    const min = parseInt(get(activeSc, 'config.minScore', '0'));
    const max = parseInt(get(activeSc, 'config.maxScore', '4'));
    const step = parseInt(get(activeSc, 'config.step', '1'));
    [...Array(max + 1).keys()].forEach((y) => {
      if (y >= min && (y + min) % step === 0) {
        const valNormalized = Math.round((y - min) / (max - min) * 100);
        const valLabel = valLabels && valLabels.find((i) => i.value === valNormalized);
        options.push({
          text: valLabel ? valLabel.label : get(activeSc, 'config.displayPercentage') ? valNormalized + '%' : y.toString(),
          val: y
        });
      }
    });

    setFilterBtns(
      hasSubScores
        ? [
          {
            val: 'listGroup',
            type: 'select',
            options: [
              { text: 'By Category', val: 'category' },
              { text: 'By Location', val: 'location' },
            ],
          },
          { val: 'value', type: 'select', options },
          {
            text: 'Instances',
            val: 'filterLocal.onlyLoc',
            type: 'switch',
            disabled: (p) => get(p, 'selected.listGroup') === 'location',
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', onlyLoc: val })),
          },
          {
            text: 'Hide N/A',
            val: 'filterLocal.hideNa',
            type: 'switch',
            disabled: (p) => get(p, 'selected.listGroup') === 'location',
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', hideNa: val })),
          },
          {
            text: 'Use Location',
            val: 'filterLocal.useLocation',
            type: 'switch',
            show: (p) => {
              const isCat = get(p, 'selected.listGroup') === 'category';
              const tier = get(p, 'assessment.data.realm.tier');
              const scard = get(p, `scorecards.${p.scorecardIndex}`);
              const scTier = get(scard, `edition.realm.tier`);
              const hasDocs = !!get(scard, 'edition.config.docs');
              return hasDocs && isCat && tier !== 'builder' && !['division', undefined].includes(scTier);
            },
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', useLocation: val })),
          },
        ]
        : [
          { val: 'value', type: 'select', options },
          { text: 'Hide N/A', val: 'filterLocal.hideNa', type: 'switch', onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', hideNa: val })) },
          {
            text: 'Use Location',
            val: 'filterLocal.useLocation',
            type: 'switch',
            show: (p) => {
              const isCat = get(p, 'selected.listGroup') === 'category';
              const tier = get(p, 'assessment.data.realm.tier');
              const scard = get(p, `scorecards.${p.scorecardIndex}`);
              const scTier = get(scard, `edition.realm.tier`);
              const hasDocs = !!get(scard, 'edition.config.docs');
              return hasDocs && isCat && tier !== 'builder' && !['division', undefined].includes(scTier);
            },
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', useLocation: val })),
          },
        ]
    );
  };

  const runFilter = async (d = scoredata) => {
    setFilterLoading(true);
    const { hasChildren } = await filterData(d, { ...filterLocal, filterHook, activeSc, search: searchString, activeEdit: activeEdit, assessment: assessment });
    setFilter(filterLocal);
    setFilterLoading(false);
    setTreed(hasChildren);
  };

  return (isCourse ? <></> : <Filters
    {...{
      assessment: props.assessment,
      buttons: filterBtns,
      setFilter: setFilterLocal,
      selected: filterLocal,
      loading: filterLoading || !size(filter),
      filterLocal,
      changeSearch: setSearchString,
      searchString: searchString,
      activeEdit: activeEdit,
      defaultFilters,

      scores: get(assessment, 'data.scores', []),
      scorecards,
      setScorecardIndex,
      scorecardIndex,
    }}
  />);
}